/**
 * `RowLabel` — a row name on one line, or on two `<tspan>`s when it wrapped (launch round,
 * 7 Oct 2026). The lines are centred on `cy` as a block, so a two-line name sits level with
 * its bar the way a one-line name does. See `labels.ts` for the rules.
 */
import type { SVGProps } from 'react';

import { LABEL_LINE_H, lineY } from './labels';

export interface RowLabelProps extends Omit<SVGProps<SVGTextElement>, 'x' | 'y'> {
  lines: readonly string[];
  x: number;
  /** The vertical centre of the row the label names. */
  cy?: number;
  /**
   * Or: where a one-line label's baseline would be, for a chart that already places its
   * text by baseline. A wrapped label's lines are centred on that same line.
   */
  baseline?: number;
  size: number;
}

export function RowLabel({ lines, x, cy: cyIn, baseline, size, ...rest }: RowLabelProps) {
  const at = (i: number, n: number) =>
    baseline !== undefined
      ? baseline + (i - (n - 1) / 2) * size * LABEL_LINE_H
      : lineY(cyIn ?? 0, size, i, n);
  if (lines.length <= 1) {
    return (
      <text x={x} y={at(0, 1)} fontSize={size} {...rest}>
        {lines[0] ?? ''}
      </text>
    );
  }
  return (
    <text x={x} y={at(0, lines.length)} fontSize={size} data-lines={lines.length} {...rest}>
      {lines.map((line, i) => (
        <tspan key={i} x={x} y={at(i, lines.length)}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

export default RowLabel;
